import { isStrictAdmin } from "@/helpers/UserHelpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type {
  ChangePage, ChatPerson, ChatSummary, ConversationView, CursorPage,
  MemberRole, MemberView, MessageInput, MessagePage, MessageView, Revision,
} from "@/interfaces/Chat";
import type { ChatSessionState } from "./chatSession";

/** actorId scopes client cache keys only. The server authenticates the Bearer token. */
export type ChatActorArgs = { actorId: string };
export type ChatConversationScope = "mine" | "others";
export type ChatConversationArgs = ChatActorArgs & { conversationId: string };
export type ChatPageArgs = { q?: string; cursor?: string; limit?: number };
export type ChatMention = { conversationId: string; messageId: string; createdAt: string };
export type CreateChatConversation =
  | { kind: "direct"; otherUserId: string }
  | { kind: "group"; name: string; memberIds: string[] };
type ChatRequest = FetchArgs & ChatActorArgs & { conversationId?: string };
type ChatState = {
  auth: { user: { id: string; roles?: string[] } | null; token: string | null };
  chatSession: ChatSessionState;
};
const http = fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URI });
const staleSession = (): { error: FetchBaseQueryError } => ({
  error: { status: "CUSTOM_ERROR", error: "CHAT_STALE_SESSION" },
});
const scopedBaseQuery: BaseQueryFn<ChatRequest, unknown, FetchBaseQueryError> = async (
  { actorId, conversationId, ...request }, api, options,
) => {
  const initial = api.getState() as ChatState;
  const { generation, conversationVersions, conversationListVersions, blocked, meetAdminVersion } = initial.chatSession;
  const listScope: ChatConversationScope | null = api.endpoint === "getChatConversations"
    ? request.params?.scope ?? "mine" : null;
  const adminSettings = request.url === "/admin/google-meet" || request.url.startsWith("/admin/google-meet/");
  const token = initial.auth.token;
  if (!token || initial.auth.user?.id !== actorId || blocked || (adminSettings && !isStrictAdmin(initial.auth.user))) return staleSession();
  const headers = new Headers(request.headers as HeadersInit | undefined);
  headers.set("Authorization", `Bearer ${token}`);
  const result = await http({ ...request, headers }, api, options);
  const current = api.getState() as ChatState;
  // Check both success and failure: a late refusal must not purge the next account.
  if (
    api.signal.aborted || current.auth.user?.id !== actorId || current.auth.token !== token ||
    current.chatSession.generation !== generation ||
    (adminSettings && (!isStrictAdmin(current.auth.user) || current.chatSession.meetAdminVersion !== meetAdminVersion)) ||
    (listScope && current.chatSession.conversationListVersions[listScope] !== conversationListVersions[listScope]) ||
    (!conversationId && current.chatSession.conversationVersions !== conversationVersions) ||
    (conversationId && current.chatSession.conversationVersions[conversationId] !== conversationVersions[conversationId])
  ) return staleSession();
  return result;
};
const conversationUrl = (id: string) => `/chat/conversations/${encodeURIComponent(id)}`;
const conversationTags = (_result: unknown, error: unknown, args: ChatConversationArgs) => error ? [] : [
  { type: "Conversations" as const, id: args.actorId },
  { type: "Conversation" as const, id: `${args.actorId}:${args.conversationId}` },
  { type: "Members" as const, id: `${args.actorId}:${args.conversationId}` },
];

export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: scopedBaseQuery,
  tagTypes: ["Conversations", "Conversation", "Members", "Summary", "Mentions"],
  endpoints: (build) => ({
    getChatUsers: build.query<CursorPage<ChatPerson & { email: string }>, ChatActorArgs & ChatPageArgs>({
      query: ({ actorId, ...params }) => ({ actorId, url: "/chat/users", params }),
    }),
    getChatConversations: build.query<CursorPage<ConversationView>, ChatActorArgs & ChatPageArgs & { scope?: ChatConversationScope }>({
      query: ({ actorId, ...params }) => ({ actorId, url: "/chat/conversations", params }),
      providesTags: (_result, _error, { actorId }) => [{ type: "Conversations", id: actorId }],
    }),
    getChatConversation: build.query<ConversationView, ChatConversationArgs>({
      query: (args) => ({ ...args, url: conversationUrl(args.conversationId) }),
      providesTags: (_result, _error, args) => [{ type: "Conversation", id: `${args.actorId}:${args.conversationId}` }],
    }),
    createChatConversation: build.mutation<ConversationView, ChatActorArgs & { body: CreateChatConversation }>({
      query: ({ actorId, body }) => ({ actorId, url: "/chat/conversations", method: "POST", body }),
      invalidatesTags: (_result, error, { actorId }) => error ? [] : [{ type: "Conversations", id: actorId }],
    }),
    renameChatConversation: build.mutation<ConversationView, ChatConversationArgs & { name: string }>({
      query: ({ name, ...args }) => ({ ...args, url: conversationUrl(args.conversationId), method: "PATCH", body: { name } }),
      invalidatesTags: conversationTags,
    }),
    getChatMembers: build.query<CursorPage<MemberView>, ChatConversationArgs & ChatPageArgs>({
      query: ({ actorId, conversationId, ...params }) => ({ actorId, conversationId, url: `${conversationUrl(conversationId)}/members`, params }),
      providesTags: (_result, _error, args) => [{ type: "Members", id: `${args.actorId}:${args.conversationId}` }],
    }),
    addChatMembers: build.mutation<ConversationView, ChatConversationArgs & { userIds: string[] }>({
      query: ({ userIds, ...args }) => ({ ...args, url: `${conversationUrl(args.conversationId)}/members`, method: "POST", body: { userIds } }),
      invalidatesTags: conversationTags,
    }),
    setChatMemberRole: build.mutation<ConversationView, ChatConversationArgs & { userId: string; role: MemberRole }>({
      query: ({ userId, role, ...args }) => ({ ...args, url: `${conversationUrl(args.conversationId)}/members/${encodeURIComponent(userId)}`, method: "PATCH", body: { role } }),
      invalidatesTags: conversationTags,
    }),
    removeChatMember: build.mutation<void, ChatConversationArgs & { userId: string }>({
      query: ({ userId, ...args }) => ({ ...args, url: `${conversationUrl(args.conversationId)}/members/${encodeURIComponent(userId)}`, method: "DELETE" }),
      invalidatesTags: conversationTags,
    }),
    getChatMessages: build.query<MessagePage, ChatConversationArgs & { before?: Revision; after?: Revision; around?: string; limit?: number }>({
      query: ({ actorId, conversationId, ...params }) => ({ actorId, conversationId, url: `${conversationUrl(conversationId)}/messages`, params }),
    }),
    getChatChanges: build.query<ChangePage, ChatConversationArgs & { after: Revision; limit?: number }>({
      query: ({ actorId, conversationId, ...params }) => ({ actorId, conversationId, url: `${conversationUrl(conversationId)}/changes`, params }),
    }),
    sendChatMessage: build.mutation<MessageView, ChatConversationArgs & { body: MessageInput }>({
      query: ({ body, ...args }) => ({ ...args, url: `${conversationUrl(args.conversationId)}/messages`, method: "POST", body }),
    }),
    setChatReaction: build.mutation<MessageView, ChatConversationArgs & { messageId: string; emoji: string; active: boolean }>({
      query: ({ messageId, emoji, active, ...args }) => ({ ...args, url: `${conversationUrl(args.conversationId)}/messages/${encodeURIComponent(messageId)}/reactions`, method: "PUT", body: { emoji, active } }),
    }),
    markChatRead: build.mutation<ConversationView, ChatConversationArgs & { throughMessageId: string }>({
      query: ({ throughMessageId, ...args }) => ({ ...args, url: `${conversationUrl(args.conversationId)}/read`, method: "PUT", body: { throughMessageId } }),
      invalidatesTags: (_result, error, { actorId }) => error ? [] : [{ type: "Summary", id: actorId }, { type: "Mentions", id: actorId }],
    }),
    getChatSummary: build.query<ChatSummary, ChatActorArgs>({
      query: ({ actorId }) => ({ actorId, url: "/chat/summary" }),
      providesTags: (_result, _error, { actorId }) => [{ type: "Summary", id: actorId }],
    }),
    getChatMentions: build.query<CursorPage<ChatMention>, ChatActorArgs & Omit<ChatPageArgs, "q"> & { page?: number }>({
      // The inbox identifies a page by depth, not a saved cursor. On refresh,
      // rebuild the chain from page one so remote reads/arrivals cannot leave
      // the mounted page anchored to an obsolete cursor.
      async queryFn({ actorId, page = 0, ...params }, api, _options, baseQuery) {
        const initial = api.getState() as ChatState;
        let cursor = params.cursor;
        for (let index = 0; ; index++) {
          const result = await baseQuery({ actorId, url: "/chat/mentions", params: { ...params, cursor } });
          const current = api.getState() as ChatState;
          if (api.signal.aborted || current.auth.token !== initial.auth.token ||
            current.chatSession.generation !== initial.chatSession.generation) return staleSession();
          if (result.error) return { error: result.error };
          const data = result.data as CursorPage<ChatMention>;
          if (index >= page || !data.nextCursor) return { data };
          if (document.hidden || navigator.onLine === false)
            return { error: { status: "CUSTOM_ERROR", error: "CHAT_REFRESH_PAUSED" } };
          cursor = data.nextCursor;
        }
      },
      providesTags: (_result, _error, { actorId }) => [{ type: "Mentions", id: actorId }],
    }),
  }),
});

export const {
  useGetChatUsersQuery, useLazyGetChatUsersQuery,
  useGetChatConversationsQuery, useLazyGetChatConversationsQuery,
  useGetChatConversationQuery, useLazyGetChatConversationQuery,
  useCreateChatConversationMutation, useRenameChatConversationMutation,
  useGetChatMembersQuery, useLazyGetChatMembersQuery,
  useAddChatMembersMutation, useSetChatMemberRoleMutation, useRemoveChatMemberMutation,
  useGetChatMessagesQuery, useLazyGetChatMessagesQuery,
  useGetChatChangesQuery, useLazyGetChatChangesQuery,
  useSendChatMessageMutation, useSetChatReactionMutation, useMarkChatReadMutation,
  useGetChatSummaryQuery, useLazyGetChatSummaryQuery,
  useGetChatMentionsQuery, useLazyGetChatMentionsQuery,
} = chatApi;

export default chatApi;
